/**
 * Movement: A* over a room grid, and where someone is along a path at a time.
 *
 * Both ends run the same code. The server is the authority (it plans every
 * path it broadcasts), the client runs it too so your own blob moves the
 * instant you click instead of a round trip later.
 */
import { BLOCKED, cellAt, OPEN, SEAT, type Grid } from './world.ts'

/** Tiles per second. */
export const SPEED = 3.2
/** Longest path the server will plan, in steps. */
export const MAX_STEPS = 160

export interface Path {
  /** Server clock, ms, when the path starts. */
  t0: number
  /** Flat [x0, y0, x1, y1, ...] in tile units (blob centres). */
  pts: number[]
}

export interface At { x: number; y: number; moving: boolean; dx: number; dy: number; /** Index of the next waypoint. */ next: number }

/** Where a walker on `path` is at `now`. */
export function posAt(path: Path, now: number): At {
  const p = path.pts
  let left = Math.max(0, now - path.t0) / 1000 * SPEED
  for (let i = 2; i < p.length; i += 2) {
    const dx = p[i]! - p[i - 2]!, dy = p[i + 1]! - p[i - 1]!
    const len = Math.hypot(dx, dy)
    if (left < len) {
      const k = left / len
      return { x: p[i - 2]! + dx * k, y: p[i - 1]! + dy * k, moving: true, dx, dy, next: i / 2 }
    }
    left -= len
  }
  const n = p.length
  const dx = n >= 4 ? p[n - 2]! - p[n - 4]! : 0, dy = n >= 4 ? p[n - 1]! - p[n - 3]! : 0
  return { x: p[n - 2]!, y: p[n - 1]!, moving: false, dx, dy, next: n / 2 }
}

/** When a path ends, in its own clock. */
export function pathEnd(path: Path): number {
  const p = path.pts
  let len = 0
  for (let i = 2; i < p.length; i += 2) len += Math.hypot(p[i]! - p[i - 2]!, p[i + 1]! - p[i - 1]!)
  return path.t0 + (len / SPEED) * 1000
}

export function standing(x: number, y: number, t0: number): Path {
  return { t0, pts: [x, y] }
}

const DIRS = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]] as const

/**
 * Shortest tile path from (sx, sy) to (gx, gy), both inclusive, as tile
 * coordinates. Diagonals never cut a blocked corner. Seats can be the goal but
 * are never walked through. Returns null when unreachable or too long.
 */
export function findPath(g: Grid, sx: number, sy: number, gx: number, gy: number): Array<[number, number]> | null {
  const goal = cellAt(g, gx, gy)
  if (goal === BLOCKED) return null
  if (cellAt(g, sx, sy) === BLOCKED) return null
  if (sx === gx && sy === gy) return [[sx, sy]]
  const W = g.w, N = g.w * g.d
  const start = sy * W + sx, end = gy * W + gx
  const gs = new Float32Array(N).fill(Infinity)
  const from = new Int32Array(N).fill(-1)
  const closed = new Uint8Array(N)
  // Binary heap of [f, idx].
  const heap: number[] = []
  const push = (f: number, i: number) => {
    heap.push(f, i)
    let c = heap.length / 2 - 1
    while (c > 0) {
      const p = (c - 1) >> 1
      if (heap[p * 2]! <= heap[c * 2]!) break
      ;[heap[p * 2], heap[c * 2]] = [heap[c * 2]!, heap[p * 2]!]
      ;[heap[p * 2 + 1], heap[c * 2 + 1]] = [heap[c * 2 + 1]!, heap[p * 2 + 1]!]
      c = p
    }
  }
  const pop = (): number => {
    const top = heap[1]!
    const lastI = heap.pop()!, lastF = heap.pop()!
    if (heap.length) {
      heap[0] = lastF; heap[1] = lastI
      let c = 0
      const n = heap.length / 2
      for (;;) {
        const l = c * 2 + 1, r = l + 1
        let m = c
        if (l < n && heap[l * 2]! < heap[m * 2]!) m = l
        if (r < n && heap[r * 2]! < heap[m * 2]!) m = r
        if (m === c) break
        ;[heap[m * 2], heap[c * 2]] = [heap[c * 2]!, heap[m * 2]!]
        ;[heap[m * 2 + 1], heap[c * 2 + 1]] = [heap[c * 2 + 1]!, heap[m * 2 + 1]!]
        c = m
      }
    }
    return top
  }
  const h = (x: number, y: number) => {
    const dx = Math.abs(x - gx), dy = Math.abs(y - gy)
    return Math.max(dx, dy) + (Math.SQRT2 - 1) * Math.min(dx, dy)
  }
  gs[start] = 0
  push(h(sx, sy), start)
  let expanded = 0
  while (heap.length) {
    const cur = pop()
    if (cur === end) break
    if (closed[cur]) continue
    closed[cur] = 1
    if (++expanded > 12000) return null
    const cx = cur % W, cy = (cur - cx) / W
    for (const [dx, dy] of DIRS) {
      const nx = cx + dx, ny = cy + dy
      const c = cellAt(g, nx, ny)
      if (c === BLOCKED) continue
      const ni = ny * W + nx
      if (c === SEAT && ni !== end) continue
      // No corner cutting: both orthogonal neighbours of a diagonal step must be open.
      if (dx && dy && (cellAt(g, cx + dx, cy) !== OPEN || cellAt(g, cx, cy + dy) !== OPEN)) continue
      const ng = gs[cur]! + (dx && dy ? Math.SQRT2 : 1)
      if (ng < gs[ni]!) {
        gs[ni] = ng
        from[ni] = cur
        push(ng + h(nx, ny), ni)
      }
    }
  }
  if (from[end] === -1) return null
  const out: Array<[number, number]> = []
  for (let i = end; i !== -1; i = from[i]!) {
    out.push([i % W, Math.floor(i / W)])
    if (out.length > MAX_STEPS + 1) return null
  }
  return out.reverse()
}

/**
 * Plan a walk from wherever `current` has its walker at `now` to tile
 * (gx, gy). The walker first finishes heading to the tile it is already
 * stepping towards, so redirecting mid-stride stays smooth.
 */
export function planWalk(g: Grid, current: Path, now: number, gx: number, gy: number): Path | null {
  const at = posAt(current, now)
  const p = current.pts
  // The tile being walked towards (or stood on).
  const nx = at.moving ? p[at.next * 2]! : at.x, ny = at.moving ? p[at.next * 2 + 1]! : at.y
  const sx = Math.floor(nx), sy = Math.floor(ny)
  const tiles = findPath(g, sx, sy, gx, gy)
  if (!tiles) return null
  const pts = [round(at.x), round(at.y)]
  for (const [x, y] of tiles) {
    const cx = x + 0.5, cy = y + 0.5
    if (Math.abs(cx - pts[pts.length - 2]!) < 1e-3 && Math.abs(cy - pts[pts.length - 1]!) < 1e-3) continue
    pts.push(cx, cy)
  }
  return { t0: now, pts }
}

const round = (v: number) => Math.round(v * 1000) / 1000

/** The tile a path ends on. */
export function endTile(path: Path): [number, number] {
  const n = path.pts.length
  return [Math.floor(path.pts[n - 2]!), Math.floor(path.pts[n - 1]!)]
}
